import React, { useState, useEffect } from "react";
import { Upload, Image as ImageIcon, Loader2, MessageSquare } from "lucide-react";
import api from "../utils/api";
import { useToast } from "../context/ToastContext";

const TAB_ORDER = ["brand-logo", "identity-avatar"];

const LogoManagement = ({ activeAiAgentId }) => {
  const { showToast } = useToast();
  const [activeTab, setActiveTab] = useState("brand-logo");

  const handleTabChange = (nextTab) => {
    if (nextTab === activeTab) return;
    setActiveTab(nextTab);
  };

  // Brand Logo State
  const [logo, setLogo] = useState(null);
  const [logoPreview, setLogoPreview] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [dragActive, setDragActive] = useState(false);
  const [isDefaultLogo, setIsDefaultLogo] = useState(false);

  // Identity Avatar (Launcher Brand Icon & Trigger) State
  const [avatar, setAvatar] = useState(null);
  const [avatarPreview, setAvatarPreview] = useState(null);
  const [isAvatarLoading, setIsAvatarLoading] = useState(false);
  const [avatarDragActive, setAvatarDragActive] = useState(false);
  const [launcherSize, setLauncherSize] = useState("60px");

  useEffect(() => {
    api.setAiAgentId(activeAiAgentId);
    fetchCurrentLogo();
    fetchCurrentAvatar();
  }, [activeAiAgentId]);

  const fetchCurrentLogo = async (forceRefresh = false) => {
    try {
      const data = await api.getCurrentLogo();
      if (data && data.exists) {
        let url = data.logo_url;
        if (forceRefresh) {
          url += (url.includes('?') ? '&' : '?') + `t=${Date.now()}`;
        }
        setLogoPreview(url);
        setIsDefaultLogo(!!data.isDefault);
      } else {
        setLogoPreview(null);
        setIsDefaultLogo(false);
      }
    } catch (error) {
      console.error("Error fetching logo:", error);
      setLogoPreview(null);
    }
  };

  const fetchCurrentAvatar = async (forceRefresh = false) => {
    try {
      const config = await api.getWidgetConfig();
      if (config) {
        if (config.launcher_size) {
          setLauncherSize(config.launcher_size);
        }
        if (config.icon_url) {
          const baseUrl = process.env.REACT_APP_API_BASE_URL?.replace("/api", "");
          let url = config.icon_url.startsWith("http")
            ? config.icon_url
            : `${baseUrl}${config.icon_url.startsWith("/") ? "" : "/"}${config.icon_url}`;
          if (forceRefresh) {
            url += (url.includes('?') ? '&' : '?') + `t=${Date.now()}`;
          }
          setAvatarPreview(url);
          return;
        }
      }
      setAvatarPreview(null);
    } catch (error) {
      console.error("Error fetching avatar:", error);
      setAvatarPreview(null);
    }
  };

  /* ---------------- BRAND LOGO HANDLERS ---------------- */
  const handleLogoUpload = (file) => {
    if (file && file.type.startsWith("image/")) {
      if (file.size > 5 * 1024 * 1024) {
        showToast("File size must be less than 5MB", "error");
        return;
      }
      setLogo(file);
      const reader = new FileReader();
      reader.onload = (e) => setLogoPreview(e.target.result);
      reader.readAsDataURL(file);
    } else {
      showToast("Please select a valid image file", "error");
    }
  };

  const handleFileInput = (event) => {
    const file = event.target.files[0];
    if (file) handleLogoUpload(file);
  };

  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") {
      setDragActive(true);
    } else if (e.type === "dragleave") {
      setDragActive(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    const file = e.dataTransfer.files[0];
    if (file) handleLogoUpload(file);
  };

  const uploadLogo = async () => {
    if (logo) {
      try {
        setIsLoading(true);
        await api.uploadLogo(logo);
        showToast("Brand logo updated successfully!");
        setTimeout(async () => {
          await fetchCurrentLogo(true);
        }, 800);
        setLogo(null);
      } catch (error) {
        console.error("Error saving logo:", error);
        showToast("Failed to upload logo. Please try again.", "error");
      } finally {
        setIsLoading(false);
      }
    }
  };

  /* ---------------- IDENTITY AVATAR HANDLERS ---------------- */
  const handleAvatarUpload = (file) => {
    if (file && file.type.startsWith("image/")) {
      if (file.size > 5 * 1024 * 1024) {
        showToast("File size must be less than 5MB", "error");
        return;
      }
      setAvatar(file);
      const reader = new FileReader();
      reader.onload = (e) => setAvatarPreview(e.target.result);
      reader.readAsDataURL(file);
    } else {
      showToast("Please select a valid image file", "error");
    }
  };

  const handleAvatarFileInput = (event) => {
    const file = event.target.files[0];
    if (file) handleAvatarUpload(file);
  };

  const handleAvatarDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") {
      setAvatarDragActive(true);
    } else if (e.type === "dragleave") {
      setAvatarDragActive(false);
    }
  };

  const handleAvatarDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setAvatarDragActive(false);
    const file = e.dataTransfer.files[0];
    if (file) handleAvatarUpload(file);
  };

  const uploadAvatar = async () => {
    if (avatar) {
      try {
        setIsAvatarLoading(true);
        await api.uploadWidgetIcon(avatar);
        showToast("Identity avatar updated successfully!");
        setTimeout(async () => {
          await fetchCurrentAvatar(true);
        }, 800);
        setAvatar(null);
      } catch (error) {
        console.error("Error saving avatar:", error);
        showToast("Failed to upload identity avatar. Please try again.", "error");
      } finally {
        setIsAvatarLoading(false);
      }
    }
  };

  return (
    <div className="flex flex-col min-h-screen bg-white font-['Inter',sans-serif] animate-slideIn pb-16">
      <div className="w-full max-w-7xl mx-auto px-4 py-5 sm:px-6 sm:py-6 lg:px-8">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between pb-5 sm:pb-6 border-b border-zinc-100 gap-3 sm:gap-4 mb-6 sm:mb-8">
          <div>
            <h1 className="text-lg sm:text-xl font-bold text-zinc-900 tracking-tight">Brand Identity & Assets</h1>
            <p className="text-xs text-zinc-500 mt-0.5 sm:mt-1">
              Customize the logo and visual identity displayed in your chat widgets, headers, and launcher triggers.
            </p>
          </div>

          {/* Minimal Tab Switcher (Matching ModelManagement.js) */}
          <div className="self-start sm:self-auto flex p-1 bg-zinc-100/80 rounded-xl border border-zinc-200/60 w-full sm:w-auto overflow-x-auto custom-scrollbar">
            <button
              type="button"
              onClick={() => handleTabChange("brand-logo")}
              className={`flex-1 sm:flex-initial px-3 sm:px-3.5 py-1.5 rounded-lg text-xs font-semibold transition-all flex items-center justify-center gap-1.5 whitespace-nowrap ${
                activeTab === "brand-logo"
                  ? "bg-white text-zinc-900 shadow-sm"
                  : "text-zinc-500 hover:text-zinc-900"
              }`}
            >
              <ImageIcon size={13} />
              <span>Brand Logo</span>
            </button>
            <button
              type="button"
              onClick={() => handleTabChange("identity-avatar")}
              className={`flex-1 sm:flex-initial px-3 sm:px-3.5 py-1.5 rounded-lg text-xs font-semibold transition-all flex items-center justify-center gap-1.5 whitespace-nowrap ${
                activeTab === "identity-avatar"
                  ? "bg-white text-zinc-900 shadow-sm"
                  : "text-zinc-500 hover:text-zinc-900"
              }`}
            >
              <MessageSquare size={13} />
              <span>Identity Avatar</span>
            </button>
          </div>
        </div>

        {/* Minimal Content Card with horizontal slide track */}
        <div className="border border-zinc-200/80 rounded-2xl bg-white shadow-xs overflow-hidden w-full">
          <div
            className="flex transition-transform duration-300 ease-out items-start"
            style={{
              transform: `translateX(-${(TAB_ORDER.indexOf(activeTab) * 100) / 2}%)`,
              width: "200%",
            }}
          >
            {/* Panel 1: Brand Logo */}
            <div className="w-1/2 flex-shrink-0 p-4 sm:p-6 md:p-8 box-border">
              <div className="flex items-center justify-between pb-4 border-b border-zinc-100 mb-6">
                <div className="flex items-center gap-3">
                  <ImageIcon className="text-zinc-700 shrink-0" size={18} />
                  <div>
                    <h2 className="text-sm font-semibold text-zinc-900">Brand Logo</h2>
                    <p className="text-xs text-zinc-500">Rendered in the chat widget header and agent response badges</p>
                  </div>
                </div>
                <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-md ${
                  isDefaultLogo
                    ? "bg-zinc-100 text-zinc-600 border border-zinc-200"
                    : "bg-emerald-50 text-emerald-700 border border-emerald-200/60"
                }`}>
                  {isDefaultLogo ? "Default Asset" : "Custom Brand"}
                </span>
              </div>

          <div className="grid grid-cols-1 md:grid-cols-5 gap-5 sm:gap-6">
            {/* Upload Section (Left) */}
            <div className="md:col-span-3 order-1">
              <div className="bg-white rounded-xl p-4 sm:p-6 border border-zinc-200 shadow-2xs flex flex-col h-full">
                <div className="flex-1 flex flex-col">
                  <div className="flex items-center justify-between mb-3 sm:mb-4">
                    <div>
                      <h3 className="font-semibold text-zinc-900 text-xs uppercase tracking-wider">Upload New Logo</h3>
                      <p className="text-[11px] text-zinc-400 mt-0.5">Drag and drop or select an image file</p>
                    </div>
                  </div>

                  <div
                    className={`
                      flex-1 flex flex-col items-center justify-center relative rounded-xl p-6 sm:p-10 border-2 border-dashed transition-all cursor-pointer text-center min-h-[200px]
                      ${dragActive
                        ? "border-zinc-900 bg-zinc-50"
                        : "border-zinc-200 bg-zinc-50/50 hover:border-zinc-300 hover:bg-zinc-50"
                      }
                    `}
                    onClick={() => document.getElementById("logo-input").click()}
                    onDragEnter={handleDrag}
                    onDragLeave={handleDrag}
                    onDragOver={handleDrag}
                    onDrop={handleDrop}
                  >
                    <div className="flex flex-col items-center justify-center w-full h-full">
                      <Upload className="text-zinc-600 shrink-0" />
                      <h4 className="text-xs font-bold text-zinc-900 mb-1">
                        {dragActive ? "Drop image to upload" : "Click to upload or drag & drop"}
                      </h4>
                      <p className="text-[10px] sm:text-[11px] text-zinc-400 mb-2.5 sm:mb-3 max-w-xs">
                        Recommended: Square PNG or SVG with transparent background
                      </p>
                      <div className="flex flex-wrap justify-center gap-1 sm:gap-1.5">
                        {["PNG", "JPG", "SVG", "WEBP"].map((ext) => (
                          <span key={ext} className="px-1.5 sm:px-2 py-0.5 bg-white rounded border border-zinc-200 text-[9px] sm:text-[10px] font-semibold text-zinc-500">
                            {ext}
                          </span>
                        ))}
                      </div>
                    </div>
                  </div>

                  <input
                    id="logo-input"
                    type="file"
                    accept="image/*"
                    onChange={handleFileInput}
                    className="hidden"
                  />
                </div>

                {logo && (
                  <div className="mt-5 p-4 bg-zinc-50/80 rounded-xl border border-zinc-200">
                    <div className="flex items-center justify-between gap-3">
                      <div className="flex items-center gap-3 min-w-0">
                        <ImageIcon className="text-zinc-600 shrink-0" />
                        <div className="min-w-0">
                          <p className="text-xs font-semibold text-zinc-900 truncate">
                            {logo.name}
                          </p>
                          <p className="text-[10px] text-zinc-400 font-medium">
                            {(logo.size / 1024 / 1024).toFixed(2)} MB • Ready to apply
                          </p>
                        </div>
                      </div>
                      <button
                        type="button"
                        onClick={() => {
                          setLogo(null);
                          fetchCurrentLogo();
                        }}
                        className="text-xs text-zinc-400 hover:text-zinc-700 font-medium px-2 py-1"
                      >
                        Clear
                      </button>
                    </div>

                    <button
                      onClick={uploadLogo}
                      disabled={isLoading}
                      className="w-full mt-4 flex items-center justify-center gap-2 px-4 py-2.5 bg-zinc-900 text-white rounded-lg hover:bg-zinc-800 disabled:opacity-50 transition-all font-semibold text-xs shadow-xs cursor-pointer active:scale-[0.99]"
                    >
                      {isLoading ? (
                        <>
                          <Loader2 size={14} className="animate-spin text-zinc-400" />
                          <span>Uploading Logo...</span>
                        </>
                      ) : (
                        <span>Save & Apply Logo</span>
                      )}
                    </button>
                  </div>
                )}
              </div>
            </div>

            {/* Active Logo Card (Right) */}
            <div className="md:col-span-2 order-2">
              <div className="bg-white rounded-xl p-5 sm:p-6 border border-zinc-200 shadow-2xs h-full flex flex-col justify-between">
                <div>
                  <div className="flex items-center justify-between mb-4">
                    <div>
                      <h3 className="font-semibold text-zinc-900 text-xs uppercase tracking-wider">Active Logo</h3>
                      <p className="text-[11px] text-zinc-400 mt-0.5">Currently rendered in widget</p>
                    </div>
                  </div>

                  <div className="w-full max-w-[240px] sm:max-w-[280px] md:max-w-none mx-auto aspect-square bg-zinc-50 rounded-xl border border-zinc-200/80 p-5 sm:p-6 flex items-center justify-center overflow-hidden">
                    {logoPreview ? (
                      <img
                        src={logoPreview}
                        alt="Current logo"
                        className="max-w-full max-h-full object-contain transition-transform duration-300 hover:scale-105"
                        onError={(e) => {
                          e.target.onerror = null;
                          e.target.src = "/default.png";
                        }}
                      />
                    ) : (
                      <div className="text-zinc-400 flex flex-col items-center gap-2">
                        <ImageIcon size={36} strokeWidth={1.5} />
                        <span className="text-xs font-medium">No Logo Configured</span>
                      </div>
                    )}
                  </div>
                </div>

                <p className="mt-3 sm:mt-4 text-[11px] sm:text-xs text-zinc-400 text-center leading-relaxed">
                  Rendered inside the user chat widget header and agent response badges.
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Panel 2: Identity Avatar */}
        <div className="w-1/2 flex-shrink-0 p-4 sm:p-6 md:p-8 box-border">
            <div className="flex items-center justify-between pb-4 border-b border-zinc-100 mb-6">
              <div className="flex items-center gap-3">
                <MessageSquare className="text-zinc-700 shrink-0" size={18} />
                <div>
                  <h2 className="text-sm font-semibold text-zinc-900">Identity Avatar</h2>
                  <p className="text-xs text-zinc-500">Launcher brand icon & trigger</p>
                </div>
              </div>
              <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-md ${
                avatarPreview
                  ? "bg-emerald-50 text-emerald-700 border border-emerald-200/60"
                  : "bg-zinc-100 text-zinc-600 border border-zinc-200"
              }`}>
                {avatarPreview ? "Custom Avatar" : "Default Icon"}
              </span>
            </div>

          <div className="grid grid-cols-1 md:grid-cols-5 gap-5 sm:gap-6">
            {/* Upload Section (Left) */}
            <div className="md:col-span-3 order-1">
              <div className="bg-white rounded-xl p-4 sm:p-6 border border-zinc-200 shadow-2xs flex flex-col h-full">
                <div className="flex-1 flex flex-col">
                  <div className="flex items-center justify-between mb-3 sm:mb-4">
                    <div>
                      <h3 className="font-semibold text-zinc-900 text-xs uppercase tracking-wider">Upload Avatar</h3>
                      <p className="text-[11px] text-zinc-400 mt-0.5">Drag and drop or select launcher trigger icon</p>
                    </div>
                  </div>

                  <div
                    className={`
                      flex-1 flex flex-col items-center justify-center relative rounded-xl p-6 sm:p-10 border-2 border-dashed transition-all cursor-pointer text-center min-h-[200px]
                      ${avatarDragActive
                        ? "border-zinc-900 bg-zinc-50"
                        : "border-zinc-200 bg-zinc-50/50 hover:border-zinc-300 hover:bg-zinc-50"
                      }
                    `}
                    onClick={() => document.getElementById("avatar-input").click()}
                    onDragEnter={handleAvatarDrag}
                    onDragLeave={handleAvatarDrag}
                    onDragOver={handleAvatarDrag}
                    onDrop={handleAvatarDrop}
                  >
                    <div className="flex flex-col items-center justify-center w-full h-full">
                      <Upload className="text-zinc-600 shrink-0" />
                      <h4 className="text-xs font-bold text-zinc-900 mb-1">
                        {avatarDragActive ? "Drop avatar to upload" : "Click to upload or drag & drop"}
                      </h4>
                      <p className="text-[10px] sm:text-[11px] text-zinc-400 mb-2.5 sm:mb-3 max-w-xs">
                        Recommended: Square PNG, SVG or WEBP with transparent background
                      </p>
                      <div className="flex flex-wrap justify-center gap-1 sm:gap-1.5">
                        {["SVG", "PNG", "JPG", "WEBP"].map((ext) => (
                          <span key={ext} className="px-1.5 sm:px-2 py-0.5 bg-white rounded border border-zinc-200 text-[9px] sm:text-[10px] font-semibold text-zinc-500">
                            {ext}
                          </span>
                        ))}
                      </div>
                    </div>
                  </div>

                  <input
                    id="avatar-input"
                    type="file"
                    accept="image/*"
                    onChange={handleAvatarFileInput}
                    className="hidden"
                  />
                </div>

                {avatar && (
                  <div className="mt-5 p-4 bg-zinc-50/80 rounded-xl border border-zinc-200">
                    <div className="flex items-center justify-between gap-3">
                      <div className="flex items-center gap-3 min-w-0">
                        <div className="w-10 h-10 bg-white rounded-lg border border-zinc-200 flex items-center justify-center shrink-0 overflow-hidden p-1">
                          {avatarPreview ? (
                            <img src={avatarPreview} alt="Avatar Preview" className="w-full h-full object-contain rounded" />
                          ) : (
                            <MessageSquare className="w-5 h-5 text-zinc-600" />
                          )}
                        </div>
                        <div className="min-w-0">
                          <p className="text-xs font-semibold text-zinc-900 truncate">
                            {avatar.name}
                          </p>
                          <p className="text-[10px] text-zinc-400 font-medium">
                            {(avatar.size / 1024 / 1024).toFixed(2)} MB • Ready to apply
                          </p>
                        </div>
                      </div>
                      <button
                        type="button"
                        onClick={() => {
                          setAvatar(null);
                          fetchCurrentAvatar();
                        }}
                        className="text-xs text-zinc-400 hover:text-zinc-700 font-medium px-2 py-1"
                      >
                        Clear
                      </button>
                    </div>

                    <button
                      onClick={uploadAvatar}
                      disabled={isAvatarLoading}
                      className="w-full mt-4 flex items-center justify-center gap-2 px-4 py-2.5 bg-zinc-900 text-white rounded-lg hover:bg-zinc-800 disabled:opacity-50 transition-all font-semibold text-xs shadow-xs cursor-pointer active:scale-[0.99]"
                    >
                      {isAvatarLoading ? (
                        <>
                          <Loader2 size={14} className="animate-spin text-zinc-400" />
                          <span>Uploading Avatar...</span>
                        </>
                      ) : (
                        <span>Save & Apply Avatar</span>
                      )}
                    </button>
                  </div>
                )}
              </div>
            </div>

            {/* Active Avatar Launcher Preview Card (Right) */}
            <div className="md:col-span-2 order-2">
              <div className="bg-white rounded-xl p-5 sm:p-6 border border-zinc-200 shadow-2xs h-full flex flex-col justify-between">
                <div>
                  <div className="flex items-center justify-between mb-4">
                    <div>
                      <h3 className="font-semibold text-zinc-900 text-xs uppercase tracking-wider">Launcher Preview</h3>
                      <p className="text-[11px] text-zinc-400 mt-0.5">Interactive trigger button preview</p>
                    </div>
                    <div className="flex items-center gap-1.5">
                      <span className={`text-[10px] font-semibold px-2 py-0.5 rounded border ${
                        avatarPreview 
                          ? "bg-emerald-50 text-emerald-700 border-emerald-200" 
                          : "bg-zinc-50 text-zinc-500 border-zinc-200"
                      }`}>
                        {avatarPreview ? "Custom Avatar" : "Default Icon"}
                      </span>
                      <span className="text-[10px] font-semibold text-zinc-400 bg-zinc-50 border border-zinc-200 px-2 py-0.5 rounded">
                        Size: {launcherSize || "60px"}
                      </span>
                    </div>
                  </div>

                  <div className="w-full max-w-[240px] sm:max-w-[280px] md:max-w-none mx-auto aspect-square bg-gradient-to-br from-zinc-50 via-zinc-100/50 to-zinc-50 rounded-xl border border-zinc-200/80 p-5 sm:p-6 flex flex-col items-center justify-center relative overflow-hidden">
                    {/* Simulated website background dots */}
                    <div className="absolute inset-0 bg-[radial-gradient(#e4e4e7_1px,transparent_1px)] [background-size:12px_12px] opacity-60"></div>

                    {/* Realistic Floating Widget Launcher Button */}
                    <div className="relative z-10 group">
                      <div
                        className={`w-16 h-16 sm:w-20 sm:h-20 flex items-center justify-center transition-all duration-300 group-hover:scale-105 cursor-pointer ${
                          avatarPreview
                            ? "rounded-2xl sm:rounded-3xl bg-zinc-900 text-white shadow-xl shadow-zinc-900/20 p-2.5 border-2 border-white ring-4 ring-zinc-100"
                            : "filter drop-shadow-md"
                        }`}
                      >
                        <img
                          src={avatarPreview || "/default-widget-icon.png"}
                          alt={avatarPreview ? "Identity Avatar" : "Default Widget Icon"}
                          className={`w-full h-full object-contain ${avatarPreview ? "rounded-xl" : ""}`}
                          onError={(e) => {
                            e.target.onerror = null;
                            e.target.src = "/default-widget-icon.png";
                          }}
                        />
                      </div>
                    </div>

                    <p className="relative z-10 mt-4 text-[10px] font-semibold text-zinc-500 uppercase tracking-wider font-semibold text-center">
                      Floating Trigger
                    </p>
                  </div>
                </div>

                <p className="mt-3 sm:mt-4 text-[11px] sm:text-xs text-zinc-400 text-center leading-relaxed">
                  Displayed as the floating launcher button trigger at the bottom corner of your website.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</div>
  );
};

export default LogoManagement;
